import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import styled, { css } from 'styled-components';
export const DefaultButtonStyles = css `
  ${ButtonReset};
  ${transition('border-color, color, background-color', 0.2)};

  --buttonHeight: ${fluid(36, 48)};
  --buttonFontSize: ${fluid(16, 18)};
  --colorPrimary: ${brand.global.white};
  --colorContrast: ${brand.global.white};
  --backgroundPrimary: ${brand.primary.purple.base};
  --backgroundContrast: ${brand.primary.purple.light};
  --disabled: #8b8b8b;

  display: inline-flex;
  align-items: center;
  border: 2px solid var(--backgroundPrimary);
  background: none;
  color: var(--colorPrimary);
  background-color: var(--backgroundPrimary);
  min-height: var(--buttonHeight);
  border-radius: 6px;
  padding: 2px ${fluid(16, 24)};
  text-decoration: none;
  user-select: none;
  width: auto;

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('border-color, color, background-color, transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  span& {
    .focus-ring & {
      outline: auto;
      outline-offset: 3px;
      outline-width: 2px;
    }

    button:hover &,
    a:hover & {
      color: var(--colorContrast);
      border-color: var(--backgroundContrast);
      background-color: var(--backgroundContrast);
    }
  }

  &:not(span) {
    cursor: pointer;

    &.focus-ring {
      outline: auto;
      outline-offset: 3px;
      outline-width: 2px;
    }

    &:hover {
      color: var(--colorContrast);
      border-color: var(--backgroundContrast);
      background-color: var(--backgroundContrast);
    }
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled,
  &[aria-disabled='true'] {
    --colorPrimary: var(--disabled);
    --colorContrast: var(--disabled);
    --backgroundPrimary: transparent;
    --backgroundContrast: transparent;

    pointer-events: none;
  }
`;
export const SecondaryButtonStyles = css `
  --colorPrimary: ${brand.global.white};
  --colorContrast: ${brand.global.white};
  --backgroundPrimary: ${brand.secondary.pink.light};
  --backgroundContrast: ${brand.secondary.pink.dark};
`;
export const TertiaryButtonStyles = css `
  --colorPrimary: ${brand.global.white};
  --colorContrast: ${brand.global.white};
  --backgroundPrimary: ${brand.tertiary.teal.light};
  --backgroundContrast: ${brand.tertiary.teal.mid};
`;
export const GhostButtonStyles = css `
  --colorPrimary: ${brand.primary.purple.light};
  --colorContrast: ${brand.global.white};
  --backgroundPrimary: transparent;
  --backgroundContrast: ${brand.primary.purple.base};

  border-color: var(--colorPrimary);
`;
export const WhiteButtonStyles = css `
  --colorPrimary: ${brand.global.white};
  --colorContrast: ${brand.primary.purple.base};
  --backgroundPrimary: transparent;
  --backgroundContrast: ${brand.global.white};
`;
export const TextOnlyButtonStyles = css `
  --buttonHeight: auto;
  --colorPrimary: ${brand.primary.purple.base};
  --colorContrast: ${brand.secondary.pink.dark};
  --backgroundPrimary: transparent;
  --backgroundContrast: transparent;

  padding: 0;
  border: none;
  border-radius: 0;
`;
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const CustomStyles = (colorPrimary, colorContrast, backgroundPrimary, backgroundContrast) => css `
  --colorPrimary: ${colorPrimary};
  --colorContrast: ${colorContrast};
  --backgroundPrimary: ${backgroundPrimary};
  --backgroundContrast: ${backgroundContrast};

  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.5;
  }
`;
const Container = styled.button `
  ${DefaultButtonStyles};

  &[data-type='secondary'] {
    ${SecondaryButtonStyles};
  }

  &[data-type='tertiary'] {
    ${TertiaryButtonStyles};
  }

  &[data-type='ghost'] {
    ${GhostButtonStyles};
  }

  &[data-type='white'] {
    ${WhiteButtonStyles};
  }

  &[data-type='text-only'] {
    ${TextOnlyButtonStyles};
  }

  &[data-type='custom'] {
    ${CustomStyles()};
  }

  &[data-icon-only='true'] {
    height: var(--buttonHeight);
    justify-content: center;
    padding: 0;
    width: var(--buttonHeight);
    border-radius: 50%;
  }

  &[data-button-size='small'] {
    --buttonHeight: ${fluid(32, 40)};
    --buttonFontSize: ${fluid(12, 14)};
  }

  &[data-button-size='large'] {
    --buttonHeight: ${fluid(48, 52)};
    --buttonFontSize: ${fluid(18, 20)};
  }
`;
export const CommonButtonTextStyles = css `
  ${transition('color', 0.2)};
  ${fonts.ubuntu.medium};

  color: currentColor;
  font-size: var(--buttonFontSize);
  line-height: 1.25em;
  text-decoration: none;
`;
const Text = styled.span `
  ${CommonButtonTextStyles};

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
const IconWrapper = styled.div `
  ${transition('color', 0.2)};

  --padding: ${fluid(14, 18)};
  --iconHeight: 17px;

  display: block;
  color: currentColor;
  height: var(--iconHeight);
  margin: 0;
  padding: 0;

  > svg {
    display: block;
    height: 100%;
  }

  [data-icon-position='left'] & {
    margin-right: auto;
    order: -1;
    padding-right: var(--padding);
  }

  [data-icon-position='right'] & {
    margin-left: auto;
    order: 1;
    padding-left: var(--padding);
  }

  [data-icon-only='true'] & {
    color: currentColor;
    height: var(--iconHeight);
    margin: 0;
    padding: 0;
    width: var(--iconHeight);
  }
`;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
};
export default ButtonStyles;
